/**
 * PendingActionRenderer — renders a `pending_action` artifact (an action the
 * agent *will* perform once approved).
 *
 * Same proposed-action layout as `ActionReceiptRenderer` but with a prominent
 * "Pending approval" badge, an Approve / Reject pair of buttons in the title
 * bar, and an `expires_at` countdown that ticks once per second. When the
 * deadline passes, the countdown switches to "Expired" and the Approve action
 * disables — Reject stays enabled so the user can dismiss it.
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useEffect, useMemo, useRef, useState } from "react"

import type {
  ActionTargetSystem,
  ActionVerb,
  Artifact,
  ArtifactAction,
  PendingActionPayload,
} from "@/api/models"
import type { LucideIcon } from "@/components/icons"

import { Calendar, CheckCircle, Clock, Edit, Folder, Mail, Ticket } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

import { ArtifactFrame } from "./ArtifactFrame"

const VERB_ICON: Record<ActionVerb, LucideIcon> = {
  sent_email: Mail,
  created_ticket: Ticket,
  updated_record: Edit,
  scheduled_meeting: Calendar,
  filed_document: Folder,
  other: CheckCircle,
}

const VERB_LABEL: Record<ActionVerb, string> = {
  sent_email: "Send email",
  created_ticket: "Create ticket",
  updated_record: "Update record",
  scheduled_meeting: "Schedule meeting",
  filed_document: "File document",
  other: "Run action",
}

const SYSTEM_LABEL: Record<ActionTargetSystem, string> = {
  m365_outlook: "Outlook",
  m365_word: "Word",
  m365_excel: "Excel",
  m365_powerpoint: "PowerPoint",
  servicedesk: "ServiceDesk",
  netbox: "NetBox",
  sharepoint: "SharePoint",
  other: "external",
}

const APPROVE_ACTION: ArtifactAction = { id: "approve", label: "Approve", primary: true }
const REJECT_ACTION: ArtifactAction = { id: "reject", label: "Reject" }

function formatRemaining(ms: number): string {
  if (ms <= 0) return "Expired"
  const totalSec = Math.floor(ms / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `Expires in ${min}:${sec.toString().padStart(2, "0")}`
}

export interface PendingActionRendererProps {
  artifact: Artifact & { payload: PendingActionPayload }
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
}

export function PendingActionRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
}: PendingActionRendererProps) {
  const { payload } = artifact
  const { proposed } = payload
  const expiresAt = payload.expires_at ?? null

  const expiryTs = useMemo(() => (expiresAt ? Date.parse(expiresAt) : null), [expiresAt])
  const [now, setNow] = useState<number>(() => Date.now())
  const isExpired = expiryTs != null && now >= expiryTs

  useEffect(() => {
    if (expiryTs == null) return undefined
    // Tick once per second to refresh the countdown text. The interval keeps
    // running after expiry — the cost is trivial (one setState per second)
    // and the alternative (clearing on expiry) needs another effect anyway.
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [expiryTs])

  // We render Approve / Reject as in-body buttons (rather than frame actions)
  // so we can disable Approve precisely when the proposal expires. Frame-level
  // actions (`ArtifactAction`) don't carry a `disabled` flag in the contract.
  // If the artifact ships its own `actions`, those still flow through the
  // frame title-bar — we only inject defaults when the artifact is empty.
  const framedArtifact = useMemo<Artifact>(() => {
    return artifact
  }, [artifact])

  const useDefaultButtons = (artifact.actions ?? []).length === 0

  const VerbIcon = VERB_ICON[proposed.verb]
  const systemLabel = SYSTEM_LABEL[proposed.target.system]

  const countdownText = expiryTs != null ? formatRemaining(expiryTs - now) : null

  // Guard against double-click: a single Approve/Reject must dispatch a
  // single `deep_artifact_decision` frame. The ref-based latch fires
  // synchronously inside the click handler so a rapid second click — even
  // before React commits the disabled state — is a no-op. We also force a
  // re-render so the buttons visibly disable after the first click.
  const dispatchedRef = useRef(false)
  const [dispatched, setDispatched] = useState(false)

  function dispatch(actionId: string) {
    if (dispatchedRef.current) return
    dispatchedRef.current = true
    setDispatched(true)
    onAction?.(actionId, artifact)
  }

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div className="flex flex-col gap-3 py-1" data-testid="pending-action-renderer-body">
        <div className="flex items-start gap-3">
          <div
            aria-hidden
            className="bg-warning/30 text-warning-ink flex size-9 shrink-0 items-center justify-center rounded-md border"
            data-testid="pending-action-verb-icon"
            data-verb={proposed.verb}
          >
            <VerbIcon className="size-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <p
                className="text-sm leading-snug font-medium"
                data-testid="pending-action-target-label"
              >
                {proposed.target.label}
              </p>
              <Badge data-testid="pending-action-badge" variant="warning">
                Pending approval
              </Badge>
            </div>
            <p className="text-muted-foreground text-xs" data-testid="pending-action-verb-label">
              {VERB_LABEL[proposed.verb]} · {systemLabel}
            </p>
            {proposed.detail ? (
              <p className="text-sm" data-testid="pending-action-detail">
                {proposed.detail}
              </p>
            ) : null}
            {countdownText ? (
              <p
                className="text-muted-foreground inline-flex items-center gap-1 text-xs"
                data-expired={isExpired ? "true" : "false"}
                data-testid="pending-action-countdown"
              >
                <Clock aria-hidden className="size-3" />
                <span>{countdownText}</span>
              </p>
            ) : null}
          </div>
        </div>
        {useDefaultButtons ? (
          <div className="flex items-center justify-end gap-2 border-t pt-3">
            <Button
              data-testid="pending-action-reject"
              disabled={dispatched}
              size="sm"
              variant="outline"
              onClick={() => dispatch(REJECT_ACTION.id)}
            >
              {REJECT_ACTION.label}
            </Button>
            <Button
              data-testid="pending-action-approve"
              disabled={isExpired || dispatched}
              size="sm"
              onClick={() => dispatch(APPROVE_ACTION.id)}
            >
              {APPROVE_ACTION.label}
            </Button>
          </div>
        ) : null}
      </div>
    </ArtifactFrame>
  )
}
